import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import './EventAccessWithCode.css';

const calcRemaining = (targetDate) => {
  const diff = new Date(targetDate) - new Date();
  if (diff <= 0) return null;
  const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
};

const EventAccessWithCode = ({ event, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const isPrivileged = user?.role === 'Admin' || user?.role === 'EventOwner';

  const [code, setCode]             = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError]           = useState('');
  const [remaining, setRemaining]   = useState(null);
  const [saleLocked, setSaleLocked] = useState(false);

  // Satış başlamadıysa geri sayım başlat
  useEffect(() => {
    const now       = new Date();
    const saleStart = event.saleStartDate ? new Date(event.saleStartDate) : null;
    const saleEnd   = event.saleEndDate   ? new Date(event.saleEndDate)   : null;
    const eventDate = new Date(event.date);

    // Etkinlik tarihi geçtiyse herkes için kilitli
    if (eventDate < now) {
      setSaleLocked(true);
      return;
    }

    // Admin/EventOwner için satış tarihi kontrolü atlanır
    if (isPrivileged) return;

    // Satış bitmişse kilitle
    if (saleEnd && saleEnd < now) {
      setSaleLocked(true);
      return;
    }

    // Satış başlamamışsa kilitle + geri sayım
    if (saleStart && saleStart > now) {
      setSaleLocked(true);
      setRemaining(calcRemaining(saleStart));

      const interval = setInterval(() => {
        const r = calcRemaining(saleStart);
        if (!r) {
          setSaleLocked(false);
          setRemaining(null);
          clearInterval(interval);
        } else {
          setRemaining(r);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [event.saleStartDate, event.saleEndDate, event.date, isPrivileged]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError('Lütfen davetiye kodunuzu girin!');
      return;
    }

    setIsValidating(true);
    try {
      const result = await api.validateInvitationCode({
        code: code.trim().toUpperCase(),
        eventId: event.id
      });

      if (result.isValid) {
        onSuccess(event, result.code, {
          isReadOnly:    result.isReadOnly    || false,
          existingSeats: result.existingSeats || [],
          reservationId: result.reservationId || null,
        });
      } else {
        setError(result.message || 'Kod geçersiz!');
      }
    } catch (err) {
      setError('Doğrulama başarısız. Lütfen tekrar deneyin.');
      console.error('Kod doğrulama hatası:', err);
    } finally {
      setIsValidating(false);
    }
  };

  // Etkinlik tarihi geçtiyse herkes kilitli, satış kapandıysa sadece normal kullanıcı
  const eventEnded = new Date(event.date) < new Date();
  const saleEnded  = !isPrivileged && event.saleEndDate && new Date(event.saleEndDate) < new Date() && !eventEnded;

  return (
    <div className="event-access-overlay">
      <div className="access-modal">
        <div className="access-header">
          <h2><i className="fa-solid fa-key" /> Davetiye Kodu Girişi</h2>
          <button onClick={onCancel} className="close-btn">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="event-info">
          <h3>{event.name}</h3>
          <p className="event-date">
            <i className="fa-solid fa-calendar" />{' '}
            {new Date(event.date).toLocaleDateString('tr-TR', {
              day: 'numeric', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })}
          </p>
        </div>

        {/* Etkinlik tarihi geçti — herkes için kilitli */}
        {new Date(event.date) < new Date() && (
          <div className="sale-locked-banner closed">
            <i className="fa-solid fa-flag-checkered" /> Etkinlik sona erdi
          </div>
        )}

        {/* Satış kapandı */}
        {saleEnded && new Date(event.date) >= new Date() && (
          <div className="sale-locked-banner closed">
            <i className="fa-solid fa-lock" /> Satışlar kapandı
          </div>
        )}

        {/* Satış başlamadı — geri sayım */}
        {saleLocked && !saleEnded && remaining && (
          <div className="sale-locked-banner not-started">
            <div className="sale-locked-title">
              <i className="fa-solid fa-clock" /> Satışlar henüz başlamadı
            </div>
            <div className="sale-locked-date">
              {new Date(event.saleStartDate).toLocaleDateString('tr-TR', {
                day: 'numeric', month: 'long', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })} tarihinde başlayacak
            </div>
            <div className="access-countdown">
              {remaining.days > 0 && (
                <div className="acd-unit">
                  <span className="acd-num">{remaining.days}</span>
                  <span className="acd-label">gün</span>
                </div>
              )}
              <div className="acd-unit">
                <span className="acd-num">{String(remaining.hours).padStart(2,'0')}</span>
                <span className="acd-label">saat</span>
              </div>
              <div className="acd-unit">
                <span className="acd-num">{String(remaining.minutes).padStart(2,'0')}</span>
                <span className="acd-label">dk</span>
              </div>
              <div className="acd-unit">
                <span className="acd-num">{String(remaining.seconds).padStart(2,'0')}</span>
                <span className="acd-label">sn</span>
              </div>
            </div>
          </div>
        )}

        {/* Admin bypass notu */}
        {isPrivileged && event.saleStartDate && new Date(event.saleStartDate) > new Date() && (
          <div className="admin-bypass-note">
            <i className="fa-solid fa-shield-halved" /> Yönetici — satış tarihi kontrolü atlandı
          </div>
        )}

        {/* Form — kilitliyse disabled */}
        <form onSubmit={handleSubmit} className="code-form">
          <div className="form-group">
            <label>Davetiye Kodunuz</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="HAMLET2026"
              maxLength="20"
              className={`code-input ${saleLocked || saleEnded ? 'input-locked' : ''}`}
              autoFocus={!saleLocked && !saleEnded}
              disabled={saleLocked || saleEnded || eventEnded}
            />
            {!saleLocked && !saleEnded && !eventEnded && (
              <p className="hint">
                <i className="fa-solid fa-circle-info" /> Davetiye kodunuzu büyük harflerle girin
              </p>
            )}
          </div>

          {error && (
            <div className="error-message">
              <i className="fa-solid fa-triangle-exclamation" /> {error}
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              <i className="fa-solid fa-xmark" /> İptal
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isValidating || saleLocked || saleEnded || eventEnded}
            >
              {isValidating
                ? <><i className="fa-solid fa-spinner fa-spin" /> Kontrol ediliyor...</>
                : <><i className="fa-solid fa-circle-check" /> Devam Et</>}
            </button>
          </div>
        </form>

        {!saleLocked && !saleEnded && !eventEnded && (
          <div className="help-text">
            <p>
              <i className="fa-solid fa-circle-info" /> Davetiye kodunuzu girerek <strong>Devam Et</strong> tuşuna veya klavyeden <strong>Enter</strong> tuşuna basın.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventAccessWithCode;